/**
 * Pure helpers for the Reports surface: the kind and audience labels, the two
 * gates (does a report leave the company; is the client report still blocked),
 * date and size formatting, the human filename for a filed report, and the
 * identity of an included item's occurrence and curation target.
 */

import { CLIENT_REPORT_ENABLED } from "../triage/client-report-gate";
import type { FiledReport } from "./report-client";
import type { ReportEntry, ReportIncludedItem, ReportKind } from "./report-types";

export const KIND_LABEL: Record<ReportKind, string> = { daily: "Daily", weekly: "Weekly", client: "Client" };

const DATE_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const STAMP_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "UTC",
});

/* One predicate used to do two different jobs — decide the rule-#7 second confirm
   AND write the row's audience label — under one name, `isClientFacing`, defined
   as `report_kind !== "daily"`. That made the label say a weekly is client-facing,
   which is false: the client is the OWNER (Red, 2026-07-28). Splitting them fixes
   the false statement without removing a safeguard (Red, 2026-07-29). */

/** THE GATE (§4 rule 7). Does this artifact leave the company?
 *
 * Weekly is in, and deliberately so. It does not reach the owner — but it goes to
 * every sub and designer on the job, naming the responsible party on each overdue
 * item. That is a document you should have to mean twice, for its own reason
 * rather than by inheriting the client report's. Daily is GC-only and goes
 * straight through. */
export function leavesTheCompany(kind: string): boolean {
  return kind === "weekly" || kind === "client";
}

/** THE LABEL. Who actually reads it — three audiences, not two. No jargon
 * (rule #10); "internal" alone was doing too much work. */
const AUDIENCE_LABEL: Record<string, string> = {
  daily: "internal",
  weekly: "internal — trades & design",
  client: "client-facing",
};
function audienceLabel(kind: string): string {
  return AUDIENCE_LABEL[kind] ?? kind;
}

/** A1 (Red, 2026-07-29): the CLIENT row is inert here too. Every other
 * client-report affordance is already disabled behind `CLIENT_REPORT_ENABLED`;
 * Reports was the last surface still offering one, and since nothing can be
 * flagged into a client report (`TW-1i-RELEASE-SETTER` ⇒ AC-1i-14, the render is
 * provably empty), that tap filed an empty client PDF into the folder Mercer
 * opens. Same lie class as the buttons already disabled. */
export function clientBlocked(kind: string): boolean {
  return kind === "client" && !CLIENT_REPORT_ENABLED;
}

function windowLabel(entry: ReportEntry): string {
  if (entry.report_kind === "daily") {
    const m = entry.window_key.match(/(\d{4}-\d{2}-\d{2})$/);
    if (m) {
      const d = new Date(m[1]);
      if (!Number.isNaN(d.getTime())) return DATE_FMT.format(d);
    }
  }
  return entry.window_key.replace(/^(daily|weekly|client)-/, "");
}

export function titleOf(entry: ReportEntry): string {
  return `${KIND_LABEL[entry.report_kind]} report — ${windowLabel(entry)}`;
}

function stampLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : STAMP_FMT.format(d);
}

export function rowMeta(entry: ReportEntry): string {
  return `Rendered ${stampLabel(entry.provenance.render_ts)} · ${audienceLabel(entry.report_kind)}`;
}

/* #19: LOCAL time, deliberately unlike `stampLabel`'s UTC pin above. A filed-at
   stamp answers "did I file this before or after that last walk-through?", and
   answering it in UTC on a Seattle jobsite gives the wrong hour — which is
   exactly the fact someone re-files on. */
const FILED_AT_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
export function filedAtLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : FILED_AT_FMT.format(d);
}

export function sizeLabel(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  return bytes < 1024 ? `${bytes} bytes` : `${Math.round(bytes / 1024)} KB`;
}

export function humanReportFilename(report: FiledReport): string {
  const base = (report.revision_of ?? report.filename).replace(
    /__gen_[0-9a-f-]{36}__rev_[0-9a-f]{64}(?=\.pdf$)/,
    "",
  );
  const ordinal = report.revision_number ?? (report.revision ? 1 : 0);
  if (ordinal <= 0) return base;
  return base.replace(/\.pdf$/i, ` Revision ${ordinal}.pdf`);
}

export function occurrenceId(item: ReportIncludedItem): string {
  return item.occurrence_id ?? `${item.curation_kind ?? "activity"}:${item.capture_id}`;
}

export function curationTargetId(item: ReportIncludedItem): string {
  return item.curation_target_id ?? item.capture_id;
}
